<template>
  <div class="scheduled-tasks" v-loading="loading">
    <header class="page-header">
      <div>
        <h1>任务监控</h1>
        <p>查看和管理市场、个股、持仓及系统健康四项固定任务。</p>
      </div>
      <div class="header-actions">
        <span class="updated-at">最近更新：{{ updatedAt || '-' }}</span>
        <el-button icon="el-icon-refresh" :loading="loading" @click="refreshData">刷新</el-button>
      </div>
    </header>

    <section class="group-grid">
      <el-card
        v-for="group in groupCards"
        :key="group.groupId"
        shadow="never"
        class="group-card"
        :class="group.tone"
      >
        <div class="group-head">
          <div class="group-icon"><i :class="group.icon"></i></div>
          <div class="status-tags">
            <el-tag size="mini" :type="configStatusType(group.configStatus)">{{ group.configStatusText }}</el-tag>
            <el-tag size="mini" effect="plain" :type="runtimeStatusType(group.runtimeStatus)">{{ group.runtimeStatusText }}</el-tag>
          </div>
        </div>

        <h3>{{ group.name }}</h3>
        <p class="group-desc">{{ group.description }}</p>

        <div class="summary-list">
          <div class="summary-row">
            <span>监控范围</span>
            <strong>{{ group.scopeSummary }}</strong>
          </div>
          <div class="summary-row">
            <span>运行时间</span>
            <strong>{{ group.scheduleSummary }}</strong>
          </div>
          <div class="summary-row">
            <span>最近运行</span>
            <strong>{{ group.latestRunTime }}</strong>
          </div>
          <div class="summary-row">
            <span>最近结果</span>
            <strong>{{ group.latestResultText }}</strong>
          </div>
        </div>

        <el-alert
          v-if="group.integrityIssues.length"
          class="integrity-alert"
          :title="group.integrityIssues.join('；')"
          type="warning"
          :closable="false"
          show-icon
        />

        <div class="card-actions">
          <el-button size="mini" @click="focusParentRuns(group.groupId)">查看记录</el-button>
          <el-button
            v-if="group.settingsPreviewSupported"
            size="mini"
            type="primary"
            plain
            @click="openSettingsPreview(group)"
          >
            设置
          </el-button>
          <el-tooltip v-else :content="group.settingsDisabledReason" placement="top">
            <span><el-button size="mini" disabled>设置</el-button></span>
          </el-tooltip>
          <el-tooltip content="正式页面父任务执行接口尚未开放" placement="top">
            <span><el-button size="mini" disabled>运行一次</el-button></span>
          </el-tooltip>
        </div>
      </el-card>
    </section>

    <el-alert
      v-if="summaryStatusWarning"
      class="integrity-banner"
      :title="summaryStatusWarning"
      type="warning"
      :closable="false"
      show-icon
    />

    <el-alert
      v-if="unexpectedExcludedTasks.length"
      class="integrity-banner"
      :title="unexpectedExcludedTaskWarning"
      type="warning"
      :closable="false"
      show-icon
    />

    <el-alert class="history-tip" type="info" :closable="false" show-icon>
      <div slot="title" class="history-tip-content">
        <span>{{ historyTipText }}</span>
        <el-button type="text" @click="goHistoryMonitor">查看历史库监控</el-button>
      </div>
    </el-alert>

    <el-card ref="parentRunsPanel" class="panel parent-runs-panel" shadow="never">
      <div slot="header" class="panel-head">
        <div>
          <strong>最近父任务运行记录</strong>
          <p>仅展示后端真实父运行回执；内部模块在详情中展开，不从旧子任务记录推测父运行。</p>
        </div>
        <el-button type="text" size="small" @click="goLegacyRunsPage">查看历史子任务记录</el-button>
      </div>

      <el-alert
        v-if="parentRunsLoadFailed"
        class="parent-runs-alert"
        title="父运行回执接口暂时不可用；页面没有把历史子任务记录伪装成父任务运行。"
        type="warning"
        :closable="false"
        show-icon
      />

      <div class="run-tabs">
        <el-radio-group v-model="selectedGroup" size="small">
          <el-radio-button label="all">全部</el-radio-button>
          <el-radio-button label="market_event_watch">市场</el-radio-button>
          <el-radio-button label="stock_event_watch">个股</el-radio-button>
          <el-radio-button label="position_context_watch">持仓</el-radio-button>
          <el-radio-button label="system_health_check">系统</el-radio-button>
        </el-radio-group>
      </div>

      <el-table
        :data="parentRecentRuns"
        size="small"
        stripe
        empty-text="尚无真实父任务运行回执"
      >
        <el-table-column label="完整父运行编号" min-width="300">
          <template slot-scope="scope">
            <div class="full-id-cell">
              <span>{{ scope.row.parent_run_id || '完整编号暂不可用' }}</span>
              <el-button
                v-if="scope.row.parent_run_id"
                type="text"
                size="mini"
                @click="copyText(scope.row.parent_run_id, '父运行编号')"
              >
                复制
              </el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="任务" min-width="165">
          <template slot-scope="scope">
            <strong>{{ groupLabelById(scope.row.group_id) }}</strong>
            <div class="sub-text">{{ triggerSourceText(scope.row.trigger_source) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="运行时间" width="165">
          <template slot-scope="scope">{{ displayTime(parentRunTimeValue(scope.row)) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag size="mini" :type="parentStatusType(scope.row.status)">{{ parentStatusText(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="结果摘要" min-width="245">
          <template slot-scope="scope">{{ parentResultText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="showParentRunDetail(scope.row)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="record-boundary-note">
        父运行回执启用前的旧记录继续保留在“历史子任务记录”中，不补写、不拼接、不猜测父运行编号。
      </div>
    </el-card>

    <notification-task-settings-preview-dialog
      :visible.sync="showSettingsDialog"
      :group="settingsGroup"
    />

    <el-dialog
      title="父任务运行详情"
      :visible.sync="showParentDetailDialog"
      width="880px"
      :close-on-click-modal="false"
    >
      <div v-loading="parentDetailLoading">
        <el-descriptions v-if="detailParent" :column="1" border size="small">
          <el-descriptions-item label="完整父运行编号">
            <div class="detail-id">
              <span>{{ detailParent.parent_run_id || '完整编号暂不可用' }}</span>
              <el-button
                v-if="detailParent.parent_run_id"
                type="text"
                size="mini"
                @click="copyText(detailParent.parent_run_id, '父运行编号')"
              >
                复制
              </el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="父任务">{{ groupLabelById(detailParent.group_id) }}</el-descriptions-item>
          <el-descriptions-item label="触发方式">{{ triggerSourceText(detailParent.trigger_source) }}</el-descriptions-item>
          <el-descriptions-item label="总体状态">
            <el-tag size="mini" :type="parentStatusType(detailParent.status)">{{ parentStatusText(detailParent.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="运行时间">{{ parentTimeRangeText(detailParent) }}</el-descriptions-item>
          <el-descriptions-item label="结果摘要">{{ parentResultText(detailParent) }}</el-descriptions-item>
          <el-descriptions-item label="运行范围">{{ detailScopeText(detailParent) }}</el-descriptions-item>
          <el-descriptions-item label="计划时间">{{ detailScheduleText(detailParent) }}</el-descriptions-item>
          <el-descriptions-item label="完整回执摘要">
            <div class="detail-id">
              <span>{{ detailParent.receipt_digest || '回执摘要暂不可用' }}</span>
              <el-button
                v-if="detailParent.receipt_digest"
                type="text"
                size="mini"
                @click="copyText(detailParent.receipt_digest, '回执摘要')"
              >
                复制
              </el-button>
            </div>
          </el-descriptions-item>
        </el-descriptions>

        <div v-if="detailChildren.length" class="children-section">
          <div class="children-title">
            <strong>内部模块</strong>
            <span>共 {{ detailChildren.length }} 个真实子运行槽位</span>
          </div>
          <el-table :data="detailChildren" size="small" stripe border>
            <el-table-column label="内部模块" min-width="120">
              <template slot-scope="scope">
                <strong>{{ scope.row.module_name || moduleLabel(scope.row.task_type) }}</strong>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template slot-scope="scope">
                <el-tag size="mini" :type="childStatusType(scope.row.child_status)">{{ childStatusText(scope.row.child_status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="完整子运行编号" min-width="250">
              <template slot-scope="scope">
                <div class="full-id-cell">
                  <span>{{ scope.row.child_run_id || '尚未生成' }}</span>
                  <el-button
                    v-if="scope.row.child_run_id"
                    type="text"
                    size="mini"
                    @click="copyText(scope.row.child_run_id, '子运行编号')"
                  >复制</el-button>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="完整批次编号" min-width="245">
              <template slot-scope="scope">
                <div class="full-id-cell">
                  <span>{{ scope.row.batch_log_id || '尚未生成' }}</span>
                  <el-button
                    v-if="scope.row.batch_log_id"
                    type="text"
                    size="mini"
                    @click="copyText(scope.row.batch_log_id, '批次编号')"
                  >复制</el-button>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="结果" min-width="210">
              <template slot-scope="scope">{{ childResultText(scope.row) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <el-alert
          v-if="detailConsistencyWarning"
          class="consistency-alert"
          :title="detailConsistencyWarning"
          type="warning"
          :closable="false"
          show-icon
        />
      </div>
      <div slot="footer">
        <el-button @click="showParentDetailDialog = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { scheduledTaskApi, firstBookApi } from '@/api'
import notificationTaskGroupApi from '@/api/notificationTaskGroup'
import NotificationTaskSettingsPreviewDialog from '@/components/NotificationTaskSettingsPreviewDialog.vue'

const GROUP_DEFS = [
  {
    groupId: 'market_event_watch',
    name: '市场事件监控',
    description: '聚合市场新闻、公告及其他市场级事件。',
    expectedModules: ['announcement_watch', 'news_watch'],
    icon: 'el-icon-news',
    tone: 'blue'
  },
  {
    groupId: 'stock_event_watch',
    name: '个股事件监控',
    description: '聚合个股新闻、董秘问答及相关个股事件。',
    expectedModules: ['stock_news_watch', 'irm_qa_watch'],
    icon: 'el-icon-aim',
    tone: 'green'
  },
  {
    groupId: 'position_context_watch',
    name: '持仓上下文监控',
    description: '跟踪第一本账当前持仓及持仓上下文变化。',
    expectedModules: [],
    icon: 'el-icon-wallet',
    tone: 'orange'
  },
  {
    groupId: 'system_health_check',
    name: '系统健康检查',
    description: '检查服务、数据源和系统关键依赖状态。',
    expectedModules: ['system_health_check'],
    icon: 'el-icon-circle-check',
    tone: 'purple'
  }
]

const ALLOWED_TASK_TYPES = [
  'announcement_watch',
  'news_watch',
  'stock_news_watch',
  'irm_qa_watch',
  'system_health_check'
]

const KNOWN_EXTERNAL_TASK_TYPES = ['hddb_history_automation', 'minute_collect']

export default {
  name: 'ScheduledTasks',
  components: {
    NotificationTaskSettingsPreviewDialog
  },
  data() {
    return {
      loading: false,
      tasks: [],
      runs: [],
      parentRuns: [],
      parentRunsLoadFailed: false,
      groupSummary: [],
      groupSummaryDataStatus: 'unknown',
      summaryLoadFailed: false,
      firstBook: {
        available: false,
        formalCount: null,
        tempCount: null,
        allCount: null
      },
      selectedGroup: 'all',
      showSettingsDialog: false,
      settingsGroup: null,
      showParentDetailDialog: false,
      parentDetailLoading: false,
      currentParentDetail: null,
      updatedAt: ''
    }
  },
  computed: {
    summaryStatusWarning() {
      if (this.summaryLoadFailed) {
        return '父任务 Summary 暂时不可用，页面已安全回退到子任务明细聚合；设置预演入口暂不开放。'
      }
      if (this.groupSummaryDataStatus === 'fallback') {
        return '父任务 Summary 暂时不可用，当前仅展示兼容只读回退数据；正式父运行回执仍仅来自只读父回执接口，设置预演入口暂不开放。'
      }
      if (this.groupSummaryDataStatus !== 'official') {
        return '父任务 Summary 数据状态暂不可确认；页面不会把未知状态显示为正常，设置预演入口暂不开放。'
      }
      return ''
    },
    normalizedTasks() {
      return this.tasks.filter(task => ALLOWED_TASK_TYPES.includes(task.task_type))
    },
    knownExternalTasks() {
      return this.tasks.filter(task => KNOWN_EXTERNAL_TASK_TYPES.includes(task.task_type))
    },
    unexpectedExcludedTasks() {
      return this.tasks.filter(task => (
        !ALLOWED_TASK_TYPES.includes(task.task_type) &&
        !KNOWN_EXTERNAL_TASK_TYPES.includes(task.task_type)
      ))
    },
    unexpectedExcludedTaskWarning() {
      if (!this.unexpectedExcludedTasks.length) return ''
      const labels = [...new Set(this.unexpectedExcludedTasks.map(task => task.task_type || '类型缺失'))]
      return `发现 ${this.unexpectedExcludedTasks.length} 个未识别后台任务，已从四任务视图安全排除：${labels.join('、')}`
    },
    historyTaskCount() {
      return this.knownExternalTasks.filter(task => task.task_type === 'hddb_history_automation').length
    },
    historyTipText() {
      if (this.historyTaskCount > 0) {
        return `历史数据更新由“历史库监控”页面独立管理，已从本页四任务视图安全排除 ${this.historyTaskCount} 个历史库自动化任务。`
      }
      return '历史数据更新由“历史库监控”页面独立管理，不归入持仓上下文任务。'
    },
    groupCards() {
      return GROUP_DEFS.map(def => this.buildGroupCard(def))
    },
    parentRecentRuns() {
      return this.parentRuns
        .filter(run => this.selectedGroup === 'all' || run.group_id === this.selectedGroup)
        .slice(0, 10)
    },
    detailParent() {
      return this.currentParentDetail && this.currentParentDetail.parent
        ? this.currentParentDetail.parent
        : null
    },
    detailChildren() {
      return this.currentParentDetail && Array.isArray(this.currentParentDetail.children)
        ? this.currentParentDetail.children
        : []
    },
    detailConsistencyWarning() {
      if (!this.currentParentDetail || !this.currentParentDetail.consistency) return ''
      const value = this.currentParentDetail.consistency
      const warnings = []
      if (value.slot_count_exact === false) warnings.push('内部模块槽位数量与父任务计划不一致')
      if (value.status_exact === false) warnings.push('父任务状态与内部模块汇总状态不一致')
      if (value.terminal_receipt_digest_present === false) warnings.push('终态父运行缺少完整回执摘要')
      return warnings.join('；')
    }
  },
  mounted() {
    this.refreshData()
  },
  methods: {
    async refreshData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadGroupSummary(),
          this.loadTasks(),
          this.loadRuns(),
          this.loadFirstBook(),
          this.loadParentRuns()
        ])
        this.updatedAt = this.nowBeijingText()
      } finally {
        this.loading = false
      }
    },
    async loadGroupSummary() {
      try {
        const res = await notificationTaskGroupApi.getSummary()
        const payload = this.unwrapResponse(res)
        this.groupSummary = Array.isArray(payload.groups) ? payload.groups : []
        this.groupSummaryDataStatus = String(payload.data_status || 'unknown').toLowerCase()
        this.summaryLoadFailed = this.groupSummary.length !== 4
      } catch (e) {
        this.groupSummary = []
        this.groupSummaryDataStatus = 'unavailable'
        this.summaryLoadFailed = true
      }
    },
    async loadTasks() {
      try {
        const res = await scheduledTaskApi.getTasks({})
        this.tasks = this.extractList(res, ['tasks', 'items', 'data'])
      } catch (e) {
        this.tasks = []
        this.$message.error('加载任务失败')
      }
    },
    async loadRuns() {
      try {
        const res = await scheduledTaskApi.getRuns({ limit: 100 })
        this.runs = this.extractList(res, ['runs', 'items', 'data'])
      } catch (e) {
        this.runs = []
      }
    },
    async loadParentRuns() {
      try {
        const res = await notificationTaskGroupApi.getParentRuns({ offset: 0, limit: 100 })
        const payload = this.unwrapResponse(res)
        this.parentRuns = Array.isArray(payload.runs) ? payload.runs : []
        this.parentRunsLoadFailed = false
      } catch (e) {
        this.parentRuns = []
        this.parentRunsLoadFailed = true
      }
    },
    async loadFirstBook() {
      try {
        const res = await firstBookApi.getCurrent()
        const payload = this.unwrapResponse(res)
        const formal = Array.isArray(payload.formal_data) ? payload.formal_data : []
        const temp = Array.isArray(payload.temp_data) ? payload.temp_data : []
        const hasFormalCount = payload.formal_count !== null && payload.formal_count !== undefined && Number.isFinite(Number(payload.formal_count))
        const hasTempCount = payload.temp_count !== null && payload.temp_count !== undefined && Number.isFinite(Number(payload.temp_count))
        const formalCount = hasFormalCount ? Number(payload.formal_count) : formal.length
        const tempCount = hasTempCount ? Number(payload.temp_count) : temp.length
        this.firstBook = {
          available: true,
          formalCount,
          tempCount,
          allCount: formalCount + tempCount
        }
      } catch (e) {
        this.firstBook = {
          available: false,
          formalCount: null,
          tempCount: null,
          allCount: null
        }
      }
    },
    unwrapResponse(res) {
      return (res && (res.machine_payload || res.data || res.payload)) || res || {}
    },
    extractList(res, keys) {
      if (Array.isArray(res)) return res
      const data = this.unwrapResponse(res)
      if (Array.isArray(data)) return data
      for (const key of keys) {
        if (Array.isArray(data[key])) return data[key]
      }
      return []
    },
    summaryGroup(groupId) {
      return this.groupSummary.find(item => item.group_id === groupId) || null
    },
    latestParentForGroup(groupId) {
      return this.parentRuns.find(run => run.group_id === groupId) || null
    },
    buildGroupCard(def) {
      const summary = this.summaryGroup(def.groupId)
      const relatedTasks = this.normalizedTasks.filter(task => def.expectedModules.includes(task.task_type))
      const relatedRuns = this.runs.filter(run => def.expectedModules.includes(run.task_type))
      const latestChildRun = relatedRuns[0] || null
      const latestParentRun = this.latestParentForGroup(def.groupId)

      if (summary) {
        const scheduleTimes = summary.schedule && Array.isArray(summary.schedule.times)
          ? summary.schedule.times
          : []
        const latest = summary.latest_child_run || null
        const integrityIssues = this.summaryIntegrityIssues(summary)
        const capabilities = summary.capabilities || {}
        const previewSupported = capabilities.configure_preview === true
        return {
          ...def,
          name: summary.display_name || def.name,
          modules: relatedTasks,
          configStatus: summary.config_status || 'incomplete',
          configStatusText: summary.config_status_text || '状态未知',
          runtimeStatus: summary.runtime_status || 'no_record',
          runtimeStatusText: summary.runtime_status_text || '状态未知',
          scopeSummary: (summary.scope && summary.scope.summary) || this.groupScopeSummary(def.groupId, relatedTasks),
          scheduleSummary: (summary.schedule && summary.schedule.summary) || this.scheduleSummary(relatedTasks),
          scheduleTimes,
          enabled: summary.config_status === 'enabled',
          latestRunTime: latestParentRun
            ? this.displayTime(this.parentRunTimeValue(latestParentRun))
            : (latest ? this.displayTime(latest.run_time_bj || latest.run_time) : '尚无记录'),
          latestResultText: latestParentRun
            ? this.parentResultText(latestParentRun)
            : (latest ? `${this.moduleLabel(latest.task_type)}：${this.childLegacyResultText(latest)}` : '尚无记录'),
          integrityIssues,
          settingsPreviewSupported: previewSupported,
          settingsDisabledReason: def.groupId === 'position_context_watch'
            ? '该父任务尚未接入'
            : '后端未开放父任务设置预演'
        }
      }

      const integrityIssues = this.groupIntegrityIssues(def, relatedTasks)
      const config = this.groupConfigStatus(def, relatedTasks, integrityIssues)
      const runtime = this.groupRuntimeStatus(def, relatedRuns, latestChildRun)
      return {
        ...def,
        modules: relatedTasks,
        configStatus: config.value,
        configStatusText: config.text,
        runtimeStatus: runtime.value,
        runtimeStatusText: runtime.text,
        scopeSummary: this.groupScopeSummary(def.groupId, relatedTasks),
        scheduleSummary: this.scheduleSummary(relatedTasks),
        scheduleTimes: this.extractScheduleTimes(relatedTasks),
        enabled: config.value === 'enabled',
        latestRunTime: latestParentRun
          ? this.displayTime(this.parentRunTimeValue(latestParentRun))
          : (latestChildRun ? this.displayTime(this.legacyRunTimeValue(latestChildRun)) : '尚无记录'),
        latestResultText: latestParentRun
          ? this.parentResultText(latestParentRun)
          : (latestChildRun ? `${this.moduleLabel(latestChildRun.task_type)}：${this.childLegacyResultText(latestChildRun)}` : '尚无记录'),
        integrityIssues,
        settingsPreviewSupported: false,
        settingsDisabledReason: this.summaryLoadFailed
          ? '父任务 Summary 暂不可用'
          : '设置预演暂不可用'
      }
    },
    summaryIntegrityIssues(summary) {
      const issues = Array.isArray(summary.integrity_issues) ? summary.integrity_issues : []
      return issues.map(item => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object') return item.message || item.code || '完整性问题'
        return '完整性问题'
      })
    },
    groupIntegrityIssues(def, tasks) {
      if (def.groupId === 'position_context_watch') return []
      const issues = []
      def.expectedModules.forEach(type => {
        const count = tasks.filter(task => task.task_type === type).length
        if (count === 0) issues.push(`缺少${this.moduleLabel(type)}模块`)
        if (count > 1) issues.push(`${this.moduleLabel(type)}存在${count}个重复任务`)
      })
      if (def.groupId === 'stock_event_watch' && tasks.length > 1) {
        const scopes = [...new Set(tasks.map(task => task.scope_type || ''))]
        if (scopes.length > 1) issues.push('内部模块范围配置不一致')
      }
      return issues
    },
    groupConfigStatus(def, tasks, issues) {
      if (def.groupId === 'position_context_watch') return { value: 'not_connected', text: '尚未接入' }
      if (issues.length) return { value: 'incomplete', text: '配置不完整' }
      const statuses = tasks.map(task => task.status)
      if (statuses.length && statuses.every(status => status === 'active')) return { value: 'enabled', text: '已启用' }
      if (statuses.length && statuses.every(status => status === 'paused')) return { value: 'disabled', text: '已停用' }
      if (statuses.length) return { value: 'partial', text: '部分启用' }
      return { value: 'incomplete', text: '配置不完整' }
    },
    groupRuntimeStatus(def, runs, latestRun) {
      if (def.groupId === 'position_context_watch') return { value: 'not_connected', text: '尚未接入' }
      if (runs.some(run => String(run.status || '').toLowerCase() === 'running')) return { value: 'running', text: '执行中' }
      if (!latestRun) return { value: 'no_record', text: '尚无记录' }
      return { value: 'idle', text: '当前空闲' }
    },
    groupScopeSummary(groupId, tasks) {
      if (groupId === 'market_event_watch') return '系统自动管理范围'
      if (groupId === 'position_context_watch') return '尚未接入'
      if (groupId === 'system_health_check') return '系统级检查'
      if (!tasks.length) return '范围暂不可用'
      const scopes = [...new Set(tasks.map(task => task.scope_type || ''))]
      if (scopes.length > 1) return '范围配置不一致'
      const scope = scopes[0]
      if (scope === 'formal_all') {
        return this.firstBook.available
          ? `第一本账正式区，当前 ${this.firstBook.formalCount} 只`
          : '第一本账正式区，数量暂不可用'
      }
      if (scope === 'first_book_all') {
        return this.firstBook.available
          ? `第一本账全范围，当前 ${this.firstBook.allCount} 只`
          : '第一本账全范围，数量暂不可用'
      }
      if (scope === 'custom') return '自定义范围'
      return '按系统配置'
    },
    extractScheduleTimes(tasks) {
      const times = []
      tasks.forEach(task => {
        String(task.schedule || '')
          .split(',')
          .map(item => item.trim())
          .filter(item => /^\d{1,2}:\d{1,2}$/.test(item))
          .forEach(item => {
            const parts = item.split(':')
            times.push(`${String(Number(parts[0])).padStart(2, '0')}:${String(Number(parts[1])).padStart(2, '0')}`)
          })
      })
      return [...new Set(times)].sort()
    },
    scheduleSummary(tasks) {
      const times = this.extractScheduleTimes(tasks)
      return times.length ? `每日 ${times.join('、')}` : '尚未配置'
    },
    openSettingsPreview(group) {
      if (!group.settingsPreviewSupported) return
      this.settingsGroup = {
        groupId: group.groupId,
        name: group.name,
        enabled: group.enabled,
        scheduleTimes: [...group.scheduleTimes]
      }
      this.showSettingsDialog = true
    },
    focusParentRuns(groupId) {
      this.selectedGroup = groupId
      this.$nextTick(() => {
        const panel = this.$refs.parentRunsPanel && this.$refs.parentRunsPanel.$el
        if (panel && panel.scrollIntoView) panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    async showParentRunDetail(row) {
      this.currentParentDetail = { parent: row, children: [], consistency: null }
      this.showParentDetailDialog = true
      this.parentDetailLoading = true
      try {
        const res = await notificationTaskGroupApi.getParentRun(row.parent_run_id)
        const payload = this.unwrapResponse(res)
        if (payload && payload.parent) {
          this.currentParentDetail = {
            parent: payload.parent,
            children: Array.isArray(payload.children) ? payload.children : [],
            consistency: payload.consistency || null
          }
        }
      } catch (e) {
        this.$message.error('加载父任务运行详情失败')
      } finally {
        this.parentDetailLoading = false
      }
    },
    parentRunTimeValue(run) {
      if (!run) return ''
      return run.started_at || run.scheduled_for || run.created_at || run.completed_at || ''
    },
    legacyRunTimeValue(run) {
      if (!run) return ''
      return run.run_time_bj || run.run_time || run.completed_at || run.created_at_bj || run.created_at || ''
    },
    parentTimeRangeText(parent) {
      if (!parent) return '-'
      const started = this.displayTime(parent.started_at || parent.scheduled_for || parent.created_at)
      const completed = parent.completed_at ? this.displayTime(parent.completed_at) : ''
      return completed && completed !== started ? `${started} 至 ${completed}` : started
    },
    parentStatusText(status) {
      const value = String(status || '').toLowerCase()
      const map = {
        pending: '等待执行',
        running: '执行中',
        success: '成功',
        partial_success: '部分成功',
        failed: '失败'
      }
      return map[value] || '未知'
    },
    parentStatusType(status) {
      const value = String(status || '').toLowerCase()
      if (value === 'success') return 'success'
      if (value === 'partial_success' || value === 'running') return 'warning'
      if (value === 'failed') return 'danger'
      return 'info'
    },
    parentResultText(run) {
      if (!run) return '结果暂不可用'
      const status = String(run.status || '').toLowerCase()
      const planned = Number(run.planned_module_count || 0)
      const success = Number(run.success_count || 0)
      const failed = Number(run.failed_count || 0)
      const skipped = Number(run.skipped_count || 0)
      const notStarted = Number(run.not_started_count || 0)
      if (status === 'success') return `成功，${success}/${planned} 个模块完成`
      if (status === 'partial_success') return `部分成功，成功 ${success}，失败 ${failed}，跳过 ${skipped}`
      if (status === 'failed') return `失败，失败 ${failed}，未启动 ${notStarted}`
      if (status === 'running') return `执行中，已成功 ${success}，失败 ${failed}`
      if (status === 'pending') return `等待执行，共 ${planned} 个模块`
      return '结果暂不可用'
    },
    childStatusText(status) {
      const value = String(status || '').toLowerCase()
      const map = {
        planned: '等待执行',
        running: '执行中',
        success: '成功',
        failed: '失败',
        skipped: '已跳过',
        not_started: '未启动'
      }
      return map[value] || '未知'
    },
    childStatusType(status) {
      const value = String(status || '').toLowerCase()
      if (value === 'success') return 'success'
      if (value === 'failed') return 'danger'
      if (value === 'running') return 'warning'
      return 'info'
    },
    childResultText(child) {
      if (!child) return '结果暂不可用'
      const status = String(child.child_status || '').toLowerCase()
      if (status === 'failed') return child.error_code ? `失败：${child.error_code}` : '执行失败'
      if (status === 'running') return '正在执行'
      if (status === 'planned') return '等待执行'
      if (status === 'skipped') return '已跳过'
      if (status === 'not_started') return '未启动'
      if (status === 'success') {
        const added = Number(child.new_items_count || 0)
        const deduped = Number(child.deduped_count || 0)
        const notified = Number(child.notified_count || 0)
        if (added || deduped || notified) return `新增 ${added}，重复过滤 ${deduped}，通知 ${notified}`
        if (child.task_type === 'system_health_check') return '系统正常'
        return '成功，无新增'
      }
      return '结果暂不可用'
    },
    childLegacyResultText(run) {
      const status = String((run && run.status) || '').toLowerCase()
      if (status === 'running') return '正在执行'
      if (status === 'pending') return '等待执行'
      if (status === 'failed' || status === 'error') return '执行失败'
      if (run && run.task_type === 'system_health_check') {
        const dataStatus = String(run.data_status || this.payloadValue(run, 'data_status') || '').toLowerCase()
        if (dataStatus === 'normal' || status === 'success') return '系统正常'
        if (dataStatus === 'warning') return '存在警告'
      }
      if (status === 'success') {
        const added = Number(run.new_items_count || 0)
        const deduped = Number(run.deduped_count || 0)
        if (added > 0 && deduped > 0) return `成功，新增 ${added} 条，重复过滤 ${deduped} 条`
        if (added > 0) return `成功，新增 ${added} 条`
        if (deduped > 0) return `成功，无新增，重复过滤 ${deduped} 条`
        return '成功，无新增'
      }
      return '结果暂不可用'
    },
    triggerSourceText(value) {
      const map = {
        manual_parent: '手动运行',
        scheduled_parent: '计划运行',
        retry_parent: '父任务重试'
      }
      return map[value] || '触发方式暂不可用'
    },
    detailScopeText(parent) {
      const snapshot = parent && parent.config_snapshot
      if (!snapshot || typeof snapshot !== 'object') return '范围暂不可用'
      const type = snapshot.scope_type
      if (type === 'formal_all') return '第一本账正式区'
      if (type === 'first_book_all') return '第一本账全范围'
      if (type === 'custom') return '自定义范围'
      if (type === 'global') return '全系统'
      return '范围暂不可用'
    },
    detailScheduleText(parent) {
      const snapshot = parent && parent.config_snapshot
      if (!snapshot || !Array.isArray(snapshot.schedule_times) || !snapshot.schedule_times.length) return '计划时间暂不可用'
      return `每日 ${snapshot.schedule_times.join('、')}`
    },
    payloadValue(run, key) {
      return run && run.machine_payload ? run.machine_payload[key] : ''
    },
    copyText(text, label) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => this.$message.success(`${label}已复制`))
      } else {
        this.$message.info(text)
      }
    },
    goLegacyRunsPage() {
      this.$router.push('/scheduled-task-runs')
    },
    goHistoryMonitor() {
      this.$router.push('/history-monitor')
    },
    groupLabelById(groupId) {
      const def = GROUP_DEFS.find(item => item.groupId === groupId)
      return def ? def.name : '未识别任务'
    },
    moduleLabel(taskType) {
      const map = {
        announcement_watch: '公告',
        news_watch: '市场新闻',
        stock_news_watch: '个股新闻',
        irm_qa_watch: '董秘问答',
        system_health_check: '系统健康'
      }
      return map[taskType] || '未识别模块'
    },
    configStatusType(status) {
      const map = {
        enabled: 'success',
        partial: 'warning',
        disabled: 'info',
        incomplete: 'danger',
        not_connected: 'info'
      }
      return map[status] || 'info'
    },
    runtimeStatusType(status) {
      const map = {
        running: 'warning',
        idle: 'success',
        no_record: 'info',
        not_connected: 'info'
      }
      return map[status] || 'info'
    },
    displayTime(value) {
      if (!value) return '-'
      const text = String(value).replace('T', ' ')
      return text.length > 19 ? text.slice(0, 19) : text
    },
    nowBeijingText() {
      try {
        const parts = new Intl.DateTimeFormat('zh-CN', {
          timeZone: 'Asia/Shanghai',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).formatToParts(new Date())
        const values = {}
        parts.forEach(part => {
          if (part.type !== 'literal') values[part.type] = part.value
        })
        return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}:${values.second}`
      } catch (e) {
        return new Date().toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false }).replace(/\//g, '-')
      }
    }
  }
}
</script>

<style scoped>
.scheduled-tasks { max-width: 1460px; margin: 0 auto; }
.page-header { min-height: 72px; padding: 8px 2px 14px; border-bottom: 1px solid #e8eef5; display: flex; justify-content: space-between; align-items: center; gap: 20px; }
.page-header h1 { margin: 0 0 7px; color: #0f172a; font-size: 27px; }
.page-header p { margin: 0; color: #64748b; }
.header-actions { display: flex; align-items: center; gap: 12px; }
.updated-at { color: #94a3b8; font-size: 12px; white-space: nowrap; }
.group-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin: 16px 0; }
.group-card { border-radius: 16px; }
.group-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
.group-icon { width: 42px; height: 42px; border-radius: 13px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 21px; }
.group-card.blue .group-icon { background: #2563eb; }
.group-card.green .group-icon { background: #16a34a; }
.group-card.orange .group-icon { background: #f59e0b; }
.group-card.purple .group-icon { background: #7c3aed; }
.status-tags { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px; }
.group-card h3 { margin: 14px 0 6px; color: #0f172a; }
.group-desc { min-height: 38px; margin: 0; color: #7c8a9d; font-size: 13px; line-height: 1.5; }
.summary-list { margin-top: 14px; padding-top: 12px; border-top: 1px solid #edf2f7; }
.summary-row { display: grid; grid-template-columns: 66px minmax(0, 1fr); gap: 8px; margin: 8px 0; font-size: 12px; }
.summary-row span { color: #94a3b8; }
.summary-row strong { color: #475569; font-weight: 600; line-height: 1.45; }
.integrity-alert { margin-top: 12px; }
.integrity-banner { margin-bottom: 14px; }
.card-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
.history-tip { margin-bottom: 14px; }
.history-tip-content { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.panel { border-radius: 16px; margin-bottom: 14px; }
.panel-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.panel-head strong { color: #0f172a; }
.panel-head p { margin: 4px 0 0; color: #7f8ea3; font-size: 12px; line-height: 1.5; }
.parent-runs-alert { margin-bottom: 14px; }
.run-tabs { margin-bottom: 14px; }
.sub-text { margin-top: 4px; color: #94a3b8; font-size: 12px; }
.full-id-cell, .detail-id { display: flex; align-items: center; justify-content: space-between; gap: 8px; color: #334155; font-family: Consolas, Monaco, monospace; word-break: break-all; }
.record-boundary-note { margin-top: 12px; color: #94a3b8; font-size: 12px; line-height: 1.6; }
.children-section { margin-top: 18px; }
.children-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.children-title strong { color: #0f172a; }
.children-title span { color: #94a3b8; font-size: 12px; }
.consistency-alert { margin-top: 14px; }
@media (max-width: 1200px) {
  .group-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 760px) {
  .page-header { flex-direction: column; align-items: flex-start; }
  .header-actions { width: 100%; justify-content: space-between; }
  .group-grid { grid-template-columns: 1fr; }
  .history-tip-content, .panel-head { align-items: flex-start; flex-direction: column; }
}
</style>
